import { useEffect, useState } from 'react';

/**
 * How long an ACTIVE table may sit un-hydrated (no gotTexas/gotOmaha, so
 * `game.tabletype` is still empty and the felt shows only the spinning HJ chip)
 * before the client declares the load failed.
 *
 * Unity (TableView.InitializeWithTimeout) gives up after a flat 10s with no
 * retry. Here `useFeltHydrationRecovery` first forces ONE reconnect at 7–10s and
 * a healthy re-subscribe hydrates ~1–2s after that, so this window is set well
 * past the recovery attempt: the modal only appears once the retry has also had
 * its chance, never on a slow-but-fine join.
 */
export const TABLE_LOAD_TIMEOUT_MS = 20000;

/**
 * Terminal failure state for a table that never loads.
 *
 * `useFeltHydrationRecovery` is a MITIGATION (one reconnect) with no outcome: if
 * the fresh connection still doesn't hydrate, nothing else fires and the HJ chip
 * spins forever with no indication of failure and no recovery path — the
 * SkullCrusher "endless loading" report. Unity, by contrast, shows
 * "Encountered an error! / There was an error in loading the table." with an
 * Exit Table action. This hook is the parity signal: it returns `true` once the
 * table has stayed un-hydrated for TABLE_LOAD_TIMEOUT_MS.
 *
 * - Clears the instant the felt hydrates (the timer is cancelled, and a table
 *   that hydrates AFTER timing out drops the failure flag too).
 * - Keyed to the gameID it fired for, so a rebind to another table never
 *   inherits a stale failure.
 * - `enabled` is the "player can see this table" gate: a parked keep-alive panel,
 *   or the grid sitting mounted behind single-table view, does not run the clock.
 *   Callers pass `useIsTableOnScreen()` (contexts/TableOnScreenContext), the same
 *   signal gating `useFeltHydrationRecovery` so the reconnect always precedes the
 *   error. It is NOT `isActiveTable` (the focused-cell gate, false for visible
 *   non-focused grid cells) and NOT `gridMode` (a layout fact, true for parked
 *   tiled panels and for the hidden grid).
 * - A panel that has NOT yet timed out starts a fresh window when it becomes
 *   enabled. A panel that ALREADY timed out stays failed across a disable /
 *   re-enable: the flag is deliberately NOT cleared on `!enabled`. The table is
 *   still un-hydrated and `useFeltHydrationRecovery`'s one-shot reconnect is
 *   already spent for that gameID, so re-arming a fresh 20s window would put the
 *   player back on the endless spinner this hook exists to end. Only hydration,
 *   a rebind to another gameID, or an EXPLICIT retry (`retryToken`) clears it.
 * - `retryToken` is the caller's "the player asked to try again" counter. Bump it
 *   and the latch drops and a fresh window arms; leave it alone and nothing about
 *   the behaviour above changes (it defaults to 0 and every existing call site
 *   omits it). It exists because the failure is deliberately sticky: without a
 *   way to clear it on purpose, the only exit from the error modal is leaving the
 *   table, which for a player who is still SEATED server-side means giving up the
 *   seat to fix a client-side render problem. Pairing a bump with a real repair
 *   (ConnectionManager.resyncTable) is what makes the retry worth offering —
 *   re-arming alone would just snooze the modal for another 20s.
 */
export function useTableLoadTimeout(
  gameID: string | undefined,
  hydrated: boolean,
  enabled = true,
  retryToken = 0
): boolean {
  // The latch carries the token it was set under, so a bump invalidates it
  // WITHOUT needing a second state write: comparing against the current token is
  // what clears the flag, and the effect re-run is what arms the new window.
  const [timedOutFor, setTimedOutFor] = useState<{ gameID: string; token: number } | null>(null);

  useEffect(() => {
    if (!gameID || hydrated) {
      setTimedOutFor(null);
      return;
    }
    if (!enabled) return;

    const id = window.setTimeout(
      () => setTimedOutFor({ gameID, token: retryToken }),
      TABLE_LOAD_TIMEOUT_MS
    );
    return () => window.clearTimeout(id);
  }, [gameID, hydrated, enabled, retryToken]);

  return !!gameID && timedOutFor?.gameID === gameID && timedOutFor.token === retryToken;
}
